import React, { useMemo, useState } from 'react';
import Icon from '../icon/icon.component';
import ClassName from '../../interfaces/class-name.interface';
import TestId from '../../interfaces/test-id.interface';
import Button from '../button/button.component';

interface Column {
  /** A unique id to identify the column */
  id: string | number;

  /** The column's title */
  title: string;
}

interface Row {
  /** A unique id to identify the row */
  id: string | number;

  /** The row's content */
  cells: Cell[];

  /** Optional click handler for the whole row */
  onClick?: (event: React.MouseEvent<HTMLTableRowElement, MouseEvent>) => void;
}

interface Cell {
  /** A unique id to identify the cell */
  id: string | number;

  /** The cell's content */
  content: React.ReactNode;

  /** An optional hash to use for table sorting */
  sortHash?: string | number;
}

export interface Props extends ClassName, TestId {
  /** The table's columns */
  cols: Column[];

  /** The table's rows */
  rows: Row[];
}

const sortIconMapping = new Map([
  [-1, 'down'],
  [1, 'up'],
]);

/** Interactive table */
const Table = ({ className = '', cols, rows, testId }: Props): React.ReactElement => {
  const [sortBy, setSortBy] = useState<number>(-1);
  const [order, setOrder] = useState<number>(1);

  const sortedRows = useMemo(() => {
    return sortBy === -1
      ? rows
      : rows.sort(
          (a, b) =>
            `${a.cells[+sortBy].sortHash || a.cells[+sortBy].content}`.localeCompare(
              `${b.cells[+sortBy].sortHash || b.cells[+sortBy].content}`,
              undefined,
              { numeric: true }
            ) * order
        );
  }, [order, rows, sortBy]);

  const sort = (by: number): void => {
    if (by === sortBy) {
      setOrder((o) => o * -1);
    } else {
      setOrder(1);
      setSortBy(by);
    }
  };

  return (
    <table className={`table table-auto w-full ${className}`} data-testid={testId}>
      <thead>
        <tr className="border-b">
          {cols.map((col, i) => (
            <th className="px-4 py-2 text-left" key={col.id}>
              <Button
                variant="plain"
                display="block"
                className="flex items-center uppercase text-black font-bold"
                onClick={(): void => sort(i)}
              >
                {col.title}
                {col.title !== '' ? (
                  <Icon
                    className="ml-1"
                    name={i === sortBy ? `sort-${sortIconMapping.get(order)}` : 'sort'}
                  />
                ) : null}
              </Button>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {sortedRows.map((row: Row, i) => (
          <tr
            key={row.id}
            className={`${i !== 0 ? 'border-t' : ''} ${row.onClick ? 'cursor-pointer' : ''}`}
            onClick={row.onClick}
          >
            {row.cells.map((cell: { id: string | number; content: React.ReactNode }) => (
              <td className="px-4 py-2" key={cell.id}>
                <span className="skeletonize">{cell.content}</span>
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default Table;
